import * as React from "react";
import { StyleSheet, Platform } from "react-native";
import Colors from "../../constants/Colors";
import { View, Text, Button } from "../../components/Themed";
import PageHeader from "../../components/PageHeader";
import { useForm } from "react-hook-form";
import Icon from "../../components/Icon";
import GlobalContext from "../../context/Global";
import { getLocalData } from "../../services/storage";
import Popup from "../../components/Popup";
import InputText from "../../components/InputText";
import { useEffect } from "react";
import appJson from "../../app.config";
import { useGetOpenHouses } from "../../hooks/useGetOpenHouses";
import Pressable from "../../components/Pressable";

export const AccountInformation = ({ navigation }: any) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm();
  const { logout, user, isTablet } = React.useContext(GlobalContext);
  const [logoutWarning, setLogoutWarning] = React.useState(false);

  const {
    data: activeOpenHouses,
    isFetching: fetchingOpenHouses,
    error: openHousesError,
  } = useGetOpenHouses("active", user?.openHouseUuid);

  const AccountInformation = (props: any) => (
    <View
      lightColor={Colors.background}
      darkColor={Colors.nuetral_grey_600}
      style={styles.container}
    >
      {props.children}
    </View>
  );

  const BackButton = () => (
    <Icon
      name="arrow-left"
      color={Colors.primary_500}
      size={24}
      onPress={() => navigation.goBack()}
    />
  );

  const PageTitle = (props: any) => (
    <Text
      size={18}
      lineHeight={24}
      weight="SemiBold"
      lightColor={Colors.nuetral_grey_700}
    >
      {props.children}
    </Text>
  );

  const LogoutButton = () => (
    <Icon
      name="log-out"
      color={Colors.primary_500}
      size={24}
      testingId="logout_button"
      onPress={() => handleLogout()}
    />
  );

  const handleLogout = async () => {
    let syncData = await getLocalData(`syncData`);
    if (syncData && syncData.length > 0) {
      setLogoutWarning(true);
    } else if (
      activeOpenHouses &&
      activeOpenHouses?.pages[0]?.openHouses.length > 0
    ) {
      setLogoutWarning(true);
    } else {
      logout();
    }
  };

  const Form = (props: any) => (
    <View style={styles.formContainer}>{props.children}</View>
  );

  const Instructions = (props: any) => (
    <Text lightColor={Colors.nuetral_grey_600}>{props.children}</Text>
  );

  const FormControls = (props: any) => (
    <View style={styles.formControls}>{props.children}</View>
  );

  useEffect(() => {
    setValue("name", `${user?.first_name || ""} ${user?.last_name || ""}`);
    setValue("email", user?.email || "");
  }, []);

  const NameTextBox = () => (
    <InputText
      placeholder="Name"
      label="Full Name"
      control={control}
      errors={errors}
      name="name"
      disabled={true}
    />
  );

  const EmailTextBox = () => (
    <InputText
      placeholder="Email"
      label="Email"
      control={control}
      errors={errors}
      name="email"
      disabled={true}
    />
  );

  const SaveButton = () => (
    <Button
      style={{ width: 186, alignSelf: "center" }}
      text="Save Changes"
      textColor={Colors.base_colors_white}
      lightColor={Colors.primary_500}
      darkColor={Colors.primary_500}
      onPress={handleSubmit((data) => console.log(data))}
    />
  );

  return (
    <AccountInformation>
      <PageHeader
        leftComponent={<BackButton />}
        centerComponent={<PageTitle>Account Information</PageTitle>}
        rightComponent={<LogoutButton />}
      ></PageHeader>
      <Form>
        <Instructions>
          Need to make changes to your account? Login to your Brivity CRM to
          edit the details below. Your updates will sync here.
        </Instructions>
        {isTablet ? (
          <FormControls>
            <NameTextBox />
            <EmailTextBox />
          </FormControls>
        ) : (
          <>
            <FormControls>
              <NameTextBox />
            </FormControls>
            <FormControls>
              <EmailTextBox />
            </FormControls>
          </>
        )}
      </Form>
      {logoutWarning && (
        <Popup
          show={logoutWarning}
          header={
            <Text size={16} weight="Bold" lightColor={Colors.nuetral_grey_700}>
              Sign Out With Pending Changes?
            </Text>
          }
          body={
            <View style={{ gap: 8 }}>
              <Text
                size={16}
                lineHeight={24}
                lightColor={Colors.nuetral_grey_700}
                style={{ textAlign: "center" }}
              >
                You have data and leads that have not synced to Brivity CRM.
                They will remain saved on this device. Sign back in to the same
                account to sync them. Do you want to sign out?
              </Text>
            </View>
          }
          footer={
            <View
              lightColor={Colors.base_colors_white}
              darkColor={Colors.nuetral_grey_700}
              style={styles.footerContainer}
            >
              <Pressable
                style={styles.button}
                onPress={() => setLogoutWarning(false)}
              >
                <Text lightColor={Colors.nuetral_grey_700} weight="Bold">
                  Cancel
                </Text>
              </Pressable>
              <Pressable
                style={styles.signOutButton}
                onPress={async () => {
                  // Preserve pending completion data for the next session.
                  setLogoutWarning(false);
                  logout();
                }}
              >
                <Text lightColor={Colors.primary_500} weight="Bold">
                  Sign Out
                </Text>
              </Pressable>
            </View>
          }
        />
      )}
      <Text style={styles.version}>{appJson.expo.version} v2</Text>
    </AccountInformation>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  formContainer: {
    padding: 24,
    width: Platform.OS === "web" ? "50%" : "100%",
    alignSelf: "center",
  },
  formControls: {
    paddingTop: 16,
    flexDirection: "row",
    gap: 16,
    height: 100,
  },
  controlContainer: {
    flex: 1,
  },
  version: {
    textAlign: "center",
    position: "absolute",
    bottom: 24,
    left: 0,
    right: 0,
    color: Colors.nuetral_grey_700,
  },
  footerContainer: {
    height: 55,
    flexDirection: "row",
    borderTopColor: Colors.tinted_grey_300,
    borderTopWidth: 1,
    paddingVertical: 8,
  },
  button: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  signOutButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    borderLeftColor: Colors.tinted_grey_300,
    borderLeftWidth: 1,
  },
});
